/**
 * Main App component
 */
import React from 'react';
import { BrowserRouter as Router, Routes, Route, Navigate } from 'react-router-dom';
import { Box, CssBaseline, Typography } from '@mui/material';
import { QueryClient, QueryClientProvider } from '@tanstack/react-query';

// i18n -- must be imported before any component that uses useTranslation
import './i18n';

// RTL-aware theme provider (handles direction + MUI theme + Emotion cache)
import RTLProvider from './i18n/RTLProvider';

// Components
import Login from './components/Auth/Login';
import Inventory from './components/Inventory/InventoryList';
import InventoryHub from './components/Inventory/InventoryHub';
import ReplenishmentHub from './components/Replenishment/ReplenishmentHub';
import AlertsPage from './components/Alerts/AlertsPage';
import AlertRulesConfig from './components/Alerts/AlertRulesConfig';
import NotificationPreferences from './components/Alerts/NotificationPreferences';
import ReportsContainer from './components/Reports/ReportsContainer';
import Dashboard from './components/Dashboard/Dashboard';
import StockOutManagement from './components/StockOut/StockOutManagement';
import ScenariosContainer from './components/Scenarios/ScenariosContainer';
import ForecastingHub from './components/Forecasting/ForecastingHub';
import ForecastCalendar from './components/Forecasting/ForecastCalendar';
import YearOverYear from './components/Forecasting/YearOverYear';
import IntegrationsHub from './components/Integration/IntegrationsHub';
import SettingsHub from './components/Settings/SettingsHub';
import UserManagement from './components/UserManagement/UserManagement';
import Layout from './components/Layout/Layout';
import OnboardingWizard from './components/Onboarding/OnboardingWizard';

// Inventory sub-pages
import StockTransfer from './components/Inventory/StockTransfer';
import StockStatus from './components/Inventory/StockStatus';

// Order Tracking (standalone page wrapper, also accessible via Reports)
import OrderTracking from './components/Replenishment/OrderTracking';

// KPI pages
import ForecastAccuracy from './components/KPI/ForecastAccuracy';
import ServiceLevelKPI from './components/KPI/ServiceLevelKPI';
import StorePerformance from './components/KPI/StorePerformance';
import CategoryPerformance from './components/KPI/CategoryPerformance';
import ReportAnalyticsHub from './components/KPI/ReportAnalyticsHub';

// Context Providers
import { FilterProvider } from './context/FilterContext';
import { ErrorProvider } from './context/ErrorContext';
import { CurrencyProvider } from './context/CurrencyContext';

// Error Handling Components
import ErrorBoundary from './components/Common/ErrorBoundary';
import ErrorSnackbar from './components/Common/ErrorSnackbar';
import { ErrorNotificationProvider } from './components/Common/ErrorNotification';

// Chatbot Widget
import ChatbotWidget from './components/Common/ChatbotWidget';

// GAP Feature Modules
import SageIntegrationDashboard from './components/SageIntegration/SageIntegrationDashboard';
import AutoOrderingSettings from './components/AutoOrdering/AutoOrderingSettings';
import TesterManagementDashboard from './components/TesterManagement/TesterManagementDashboard';
import ABCClassificationDashboard from './components/Analytics/ABCClassificationDashboard';
import PromotionManagement from './components/Promotions/PromotionManagement';
import GWPManagement from './components/GWP/GWPManagement';
import AdvancedAnalyticsDashboard from './components/Analytics/AdvancedAnalyticsDashboard';
import BatchInventoryDashboard from './components/BatchInventory/BatchInventoryDashboard';
import AIInsightsDashboard from './components/Analytics/AIInsightsDashboard';

// NOTE: Theme configuration has moved into RTLProvider (frontend/src/i18n/RTLProvider.tsx)
// so that the MUI theme direction can be dynamically switched between LTR and RTL
// when the user changes the active language.

// React Query client
const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      refetchOnWindowFocus: false,
      retry: 1,
    },
  },
});

// Protected route component
const ProtectedRoute: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  // Since we're using httpOnly cookies for tokens, check for user session instead
  const user = sessionStorage.getItem('user');

  if (!user) {
    return <Navigate to="/login" replace />;
  }

  return <>{children}</>;
};

function App() {
  return (
    <QueryClientProvider client={queryClient}>
      {/* RTLProvider replaces the previous ThemeProvider.
          It creates an MUI theme with the correct `direction` based on the
          active i18n language and wraps children in a CacheProvider so that
          Emotion-generated styles are automatically flipped for RTL. */}
      <RTLProvider>
        <CssBaseline />
        <ErrorProvider>
          <ErrorNotificationProvider>
            <ErrorBoundary>
              <CurrencyProvider>
              <Router future={{ v7_startTransition: true, v7_relativeSplatPath: true }}>
                {/* FilterProvider must be inside Router so it can use useSearchParams for URL sync */}
                <FilterProvider>
                  <Routes>
                    <Route path="/login" element={<Login />} />
                    <Route
                      path="/onboarding"
                      element={
                        <ProtectedRoute>
                          <OnboardingWizard />
                        </ProtectedRoute>
                      }
                    />

                    <Route
                      path="/"
                      element={
                        <ProtectedRoute>
                          <Layout />
                        </ProtectedRoute>
                      }
                    >
                      <Route index element={<Dashboard />} />
                      <Route path="dashboard" element={<Dashboard />} />

                      {/* Forecasting — tab-based routing */}
                      <Route path="forecasting" element={<Navigate to="/forecasting/sku-projection" replace />} />
                      {/* Sales Forecast removed — redirect to SKU Projection */}
                      <Route path="forecasting/sales-forecast" element={<Navigate to="/forecasting/sku-projection" replace />} />
                      <Route path="forecasting/sku-projection" element={<ForecastingHub />} />
                      <Route path="forecasting/manual-override" element={<ForecastingHub />} />
                      <Route path="forecasting/new-product-forecast" element={<ForecastingHub />} />
                      <Route path="forecasting/cannibalization" element={<ForecastingHub />} />
                      <Route path="forecasting/year-over-year" element={<ForecastingHub />} />
                      <Route path="forecasting/seasonal-profiles" element={<ForecastingHub />} />
                      <Route path="forecasting/settings" element={<ForecastingHub />} />
                      {/* Legacy routes — redirect to new tab-based paths */}
                      <Route path="forecasting-calendar" element={<ForecastCalendar />} />
                      <Route path="forecasting-yoy" element={<Navigate to="/forecasting/year-over-year" replace />} />

                      {/* Replenishment — tab-based routing */}
                      <Route path="replenishment" element={<Navigate to="/replenishment/projection" replace />} />
                      <Route path="replenishment/projection" element={<ReplenishmentHub />} />
                      <Route path="replenishment/stock-transfer" element={<ReplenishmentHub />} />
                      <Route path="replenishment/transfer-history" element={<ReplenishmentHub />} />
                      <Route path="replenishment/order-validation" element={<ReplenishmentHub />} />
                      <Route path="replenishment/order-history" element={<ReplenishmentHub />} />
                      <Route path="replenishment/settings" element={<ReplenishmentHub />} />
                      <Route path="order-tracking" element={<OrderTracking />} />

                      {/* Inventory — tab-based routing */}
                      <Route path="inventory" element={<InventoryHub />} />
                      <Route path="inventory/stockout-management" element={<InventoryHub />} />
                      <Route path="inventory/stock-status" element={<InventoryHub />} />
                      <Route path="inventory/batch-tracking" element={<InventoryHub />} />
                      {/* Legacy inventory routes — redirect to new tab paths */}
                      <Route path="inventory/stock-transfer" element={<Navigate to="/replenishment/stock-transfer" replace />} />
                      <Route path="inventory/transfer-history" element={<Navigate to="/replenishment/transfer-history" replace />} />
                      <Route path="stockout-management" element={<Navigate to="/inventory/stockout-management" replace />} />
                      <Route path="stock-transfer" element={<Navigate to="/replenishment/stock-transfer" replace />} />
                      <Route path="stock-status" element={<Navigate to="/inventory/stock-status" replace />} />
                      <Route path="batch-inventory" element={<Navigate to="/inventory/batch-tracking" replace />} />

                      {/* Report Analytics — tab-based routing */}
                      <Route path="report-analytics" element={<Navigate to="/report-analytics/kpi/forecast-accuracy" replace />} />
                      <Route path="report-analytics/kpi/forecast-accuracy" element={<ReportAnalyticsHub />} />
                      <Route path="report-analytics/kpi/service-level" element={<ReportAnalyticsHub />} />
                      <Route path="report-analytics/business-analysis/store-performance" element={<ReportAnalyticsHub />} />
                      <Route path="report-analytics/business-analysis/category-performance" element={<ReportAnalyticsHub />} />
                      <Route path="report-analytics/business-analysis/abc-classification" element={<ReportAnalyticsHub />} />
                      <Route path="report-analytics/business-analysis/ai-insights" element={<ReportAnalyticsHub />} />
                      {/* Legacy redirects from /reports/* */}
                      <Route path="reports/forecast-accuracy" element={<Navigate to="/report-analytics/kpi/forecast-accuracy" replace />} />
                      <Route path="reports/service-level" element={<Navigate to="/report-analytics/kpi/service-level" replace />} />
                      <Route path="reports/store-performance" element={<Navigate to="/report-analytics/business-analysis/store-performance" replace />} />
                      <Route path="reports/category-performance" element={<Navigate to="/report-analytics/business-analysis/category-performance" replace />} />
                      <Route path="reports/abc-classification" element={<Navigate to="/report-analytics/business-analysis/abc-classification" replace />} />

                      {/* Settings */}
                      <Route path="settings" element={<SettingsHub />} />
                      <Route path="user-management" element={<UserManagement />} />

                      {/* GAP Feature Modules */}
                      <Route path="sage-integration" element={<SageIntegrationDashboard />} />
                      <Route path="auto-ordering" element={<AutoOrderingSettings />} />
                      <Route path="tester-management" element={<TesterManagementDashboard />} />
                      <Route path="abc-classification" element={<ABCClassificationDashboard />} />
                      <Route path="promotions" element={<PromotionManagement />} />
                      <Route path="gwp-management" element={<GWPManagement />} />
                      <Route path="advanced-analytics" element={<AdvancedAnalyticsDashboard />} />
                      <Route path="batch-inventory" element={<BatchInventoryDashboard />} />

                      {/* Other feature pages (accessible but not in main sidebar) */}
                      <Route path="scenarios" element={<ScenariosContainer />} />
                      <Route path="reports" element={<ReportsContainer />} />
                      <Route path="integrations" element={<IntegrationsHub />} />
                      <Route path="alerts" element={<AlertsPage />} />
                      <Route path="alert-rules" element={<AlertRulesConfig />} />
                      <Route path="notification-preferences" element={<NotificationPreferences />} />

                      {/* 404 catch-all */}
                      <Route path="*" element={
                        <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', minHeight: '60vh' }}>
                          <Typography variant="h4" gutterBottom>Page Not Found</Typography>
                          <Typography variant="body1" color="text.secondary">The page you're looking for doesn't exist.</Typography>
                        </Box>
                      } />
                    </Route>
                  </Routes>
                </FilterProvider>
              </Router>
              </CurrencyProvider>
              {/* Global error snackbar for API errors */}
              <ErrorSnackbar />
              {/* Floating chatbot widget */}
              <ChatbotWidget />
            </ErrorBoundary>
          </ErrorNotificationProvider>
        </ErrorProvider>
      </RTLProvider>
    </QueryClientProvider>
  );
}

export default App;
